import React, { useState, useRef } from 'react';
import { getFirestore, collection, query, where, serverTimestamp, doc, deleteDoc, updateDoc, setDoc, getDocs, writeBatch } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, ref, getDownloadURL, deleteObject, uploadBytesResumable } from 'firebase/storage';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  Container,
  Paper
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Share as ShareIcon,
  DriveFileMove as DriveFileMoveIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';

// Shared Components
import ViewModeToggle from '../shared/ViewModeToggle';
import BreadcrumbNav from '../shared/BreadcrumbNav';
import FileItem from '../shared/FileItem';
import UploadDialog from '../shared/UploadDialog';
import ConfirmDialog from '../shared/ConfirmDialog';
import MoveDialog from '../shared/MoveDialog';
import ShareDialog from '../shared/ShareDialog';

// Custom Hooks
import useAuth from '../../hooks/useAuth';
import { useVaultItems } from '../../hooks/useVaultItems';

// Utils
import { generateId, buildStoragePath } from '../../utils/helpers';
import logger from '../../utils/logger';

const Documents = () => {
  const [currentPath, setCurrentPath] = useState('');
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  const [gridSize, setGridSize] = useState('medium');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadErrors, setUploadErrors] = useState({});
  
  const db = getFirestore();
  const auth = getAuth();
  const storage = getStorage();
  const { currentUser } = useAuth();
  const fileInputRef = useRef();

  // Use the shared useVaultItems hook
  const { items: documents, loading, error } = useVaultItems({
    userId: currentUser?.uid,
    collectionType: 'documents',
    currentPath,
    isPersonal: true
  });

  // Handlers
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const folderRef = doc(db, 'users', auth.currentUser.uid, 'documents', generateId());
      await setDoc(folderRef, {
        name: newFolderName.trim(),
        type: 'folder',
        path: currentPath ? `${currentPath}/${newFolderName.trim()}` : newFolderName.trim(),
        timestamp: serverTimestamp(),
      });
      
      setNewFolderName('');
      setNewFolderDialogOpen(false);
    } catch (error) {
      logger.error('Error creating folder:', error);
    }
  };

  const handleUpload = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
      setUploadDialogOpen(true);
    }
  };

  const handleUploadConfirm = async () => {
    for (const file of selectedFiles) {
      try {
        const storagePath = buildStoragePath({
          uid: auth.currentUser.uid,
          collectionType: 'documents',
          currentPath,
          fileName: file.name,
          isPersonal: true
        });
        const storageRef = ref(storage, storagePath);

        // Add metadata to the upload
        const metadata = {
          customMetadata: {
            visibility: 'shared',
            familyCode: localStorage.getItem('familyCode'),
            uploadedBy: auth.currentUser.uid,
            uploadedAt: new Date().toISOString()
          }
        };

        const uploadTask = uploadBytesResumable(storageRef, file, metadata);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: progress,
            }));
          },
          (error) => {
            logger.error('Error uploading file:', error);
            setUploadErrors((prev) => ({
              ...prev,
              [file.name]: error.message,
            }));
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            const fileRef = doc(db, 'users', auth.currentUser.uid, 'documents', generateId());
            await setDoc(fileRef, {
              name: file.name,
              type: 'file',
              path: currentPath ? `${currentPath}/${file.name}` : file.name,
              url: downloadURL,
              size: file.size,
              contentType: file.type,
              timestamp: serverTimestamp(),
              visibility: 'shared',
              familyCode: localStorage.getItem('familyCode'),
              uploadedBy: auth.currentUser.uid,
              uploadedAt: serverTimestamp()
            });

            setUploadProgress((prev) => {
              const { [file.name]: removed, ...rest } = prev;
              return rest;
            });
            setUploadErrors((prev) => {
              const { [file.name]: removed, ...rest } = prev;
              return rest;
            });
          }
        );
      } catch (error) {
        logger.error('Error starting upload:', error);
        setUploadErrors((prev) => ({
          ...prev,
          [file.name]: error.message,
        }));
      }
    }
  };

  const handleDelete = async (item) => {
    try {
      if (item.type === 'file') {
        const storageRef = ref(storage, item.url);
        await deleteObject(storageRef);
      }

      const itemRef = doc(db, 'users', auth.currentUser.uid, 'documents', item.id);
      await deleteDoc(itemRef);

      if (item.type === 'folder') {
        const q = query(
          collection(db, 'users', auth.currentUser.uid, 'documents'),
          where('path', '>=', item.path),
          where('path', '<', item.path + '\uf8ff')
        );
        const querySnapshot = await getDocs(q);
        const batch = writeBatch(db);
        querySnapshot.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }
    } catch (error) {
      logger.error('Error deleting item:', error);
    }
  };

  const handleMove = async (item, newPath) => {
    try {
      const itemRef = doc(db, 'users', auth.currentUser.uid, 'documents', item.id);
      const newItemPath = newPath ? `${newPath}/${item.name}` : item.name;
      
      await updateDoc(itemRef, {
        path: newItemPath,
      });

      if (item.type === 'folder') {
        const q = query(
          collection(db, 'users', auth.currentUser.uid, 'documents'),
          where('path', '>=', item.path),
          where('path', '<', item.path + '\uf8ff')
        );
        const querySnapshot = await getDocs(q);
        const batch = writeBatch(db);
        querySnapshot.forEach((doc) => {
          const docData = doc.data();
          const relativePath = docData.path.slice(item.path.length);
          batch.update(doc.ref, {
            path: newItemPath + relativePath,
          });
        });
        await batch.commit();
      }
    } catch (error) {
      logger.error('Error moving item:', error);
    }
  };

  // Menu handlers
  const handleMenuClick = (event, item) => {
    event.stopPropagation(); // Prevent item click
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleMoveClick = () => {
    handleMenuClose();
    setMoveDialogOpen(true);
  };

  const handleShareClick = () => {
    handleMenuClose();
    setShareDialogOpen(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setConfirmDialogOpen(true);
  };

  const handleItemClick = (item) => {
    if (item.type === 'folder') {
      setCurrentPath(item.path);
    } else if (item.url) {
      window.open(item.url, '_blank');
    }
  };

  if (!auth.currentUser) {
    return (
      <Container>
        <Typography color="error" align="center">
          Please sign in to access your documents
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box className="file-explorer-header">
          <Typography variant="h5">My Documents</Typography>
          <Box display="flex" gap={2}>
            <ViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              gridSize={gridSize}
              onGridSizeChange={setGridSize}
            />
            <Button
              variant="contained"
              startIcon={<DescriptionIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Documents
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
              onChange={handleUpload}
            />
          </Box>
        </Box>

        <BreadcrumbNav
          currentPath={currentPath}
          onNavigate={setCurrentPath}
          rootLabel="Documents"
        />

        {error && (
          <Typography color="error" className="error-message">
            {error}
          </Typography>
        )}

        {loading ? (
          <Box className="loading-container">
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <Typography align="center" className="empty-state">
            This folder is empty
          </Typography>
        ) : viewMode === 'grid' ? (
          <Box className="file-grid" sx={{ gridTemplateColumns: `repeat(${gridSize === 'small' ? 6 : gridSize === 'large' ? 3 : 4}, 1fr)` }}>
            {documents.map(item => (
              <FileItem
                key={item.id}
                item={item}
                layout="grid"
                onClick={handleItemClick}
                onMenuClick={(e, item) => handleMenuClick(e, item)}
              />
            ))}
          </Box>
        ) : (
          <Box className="file-list-view">
            {documents.map(item => (
              <FileItem
                key={item.id}
                item={item}
                layout="list"
                onClick={handleItemClick}
                onMenuClick={(e, item) => handleMenuClick(e, item)}
              />
            ))}
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMoveClick}>
          <DriveFileMoveIcon sx={{ mr: 1 }} /> Move
        </MenuItem>
        <MenuItem onClick={handleShareClick}>
          <ShareIcon sx={{ mr: 1 }} /> Share
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      <MoveDialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        currentPath={currentPath}
        onMove={(newPath) => {
          if (selectedItem) {
            handleMove(selectedItem, newPath);
            setMoveDialogOpen(false);
          }
        }}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        item={selectedItem}
        onShare={async (shareSettings) => {
          if (selectedItem) {
            try {
              const itemRef = doc(db, 'users', auth.currentUser.uid, 'documents', selectedItem.id);
              await updateDoc(itemRef, {
                visibility: shareSettings.visibility,
                familyCode: shareSettings.familyCode || localStorage.getItem('familyCode')
              });
              setShareDialogOpen(false);
            } catch (error) {
              logger.error('Error updating share settings:', error);
            }
          }
        }}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        title="Delete Item"
        content={`Are you sure you want to delete "${selectedItem?.name}"? This action cannot be undone.`}
        onConfirm={() => {
          if (selectedItem) {
            handleDelete(selectedItem);
            setConfirmDialogOpen(false);
          }
        }}
      />

      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
          setSelectedFiles([]);
          setUploadProgress({});
          setUploadErrors({});
        }}
        files={selectedFiles}
        progress={uploadProgress}
        errors={uploadErrors}
        onConfirm={handleUploadConfirm}
      />

      <Dialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
      >
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFolderDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Documents;

